import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchCategories,
  fetchPartsBySingleCategory
} from "../store/categories/categoriesAction";
import PartCard from "@/components/PartCard";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import CardShimmer from "../hooks/CardShimmer";

const PartsByCategory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [partsLoading, setPartsLoading] = useState(true);

  const {
    categories,
    partsBySingleCategory,
    singleCategoryLoading,
    singleCategoryLastVisible,
    singleCategoryHasMore,
    singleCategoryLoadingMore
  } = useSelector((state) => state.categories);

  const handleLoadMore = async () => {
    if (!singleCategoryHasMore || singleCategoryLoadingMore) return;

    try {
      await dispatch(
        fetchPartsBySingleCategory({
          categoryId,
          lastVisible: singleCategoryLastVisible,
          isLoadMore: true
        })
      ).unwrap();
    } catch (error) {
      console.error("Load more error:", error);
    }
  };

  useEffect(() => {
    if (!categories || categories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, categories]);

  const currentCategory = useMemo(
    () => categories.find((cat) => cat.id === categoryId),
    [categories, categoryId]
  );

  useEffect(() => {
    const fetchInitialParts = async () => {
      try {
        setPartsLoading(true);
        await dispatch(fetchPartsBySingleCategory({ categoryId })).unwrap();
      } catch (err) {
        console.error("Initial fetch error:", err);
      } finally {
        setPartsLoading(false);
      }
    };

    if (categoryId) fetchInitialParts();
  }, [categoryId]);

  return (
    <motion.div className="space-y-12 mt-5 w-full">
      {partsLoading ? (
        // <div className="flex justify-center items-center min-h-[60vh]">
        //   <Loader2 className="h-12 w-12 animate-spin text-primary" />
        // </div>
        <CardShimmer row={1} />
      ) : (
        <>
          <Button
            variant="outline"
            onClick={() => navigate("/listings")}
            className="mb-6 flex items-center justify-start"
          >
            <ArrowLeft className="mr-2 mt-0.5 h-4 w-4" /> Back to Listings
          </Button>

          <motion.h1 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text pb-1">
            {currentCategory?.name || "Category"}
          </motion.h1>

          {!partsLoading && partsBySingleCategory?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {partsBySingleCategory.map((part) => (
                <PartCard key={part.id} part={part} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center text-lg">
              No parts available for this category yet.
            </p>
          )}

          {singleCategoryHasMore && (
            <div className="flex items-center justify-center mt-6">
              <Button
                variant="outline"
                className="px-6 py-2"
                onClick={handleLoadMore}
                disabled={singleCategoryLoadingMore || partsLoading}
              >
                {singleCategoryLoadingMore && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {singleCategoryLoadingMore ? "Loading..." : "Load More"}
              </Button>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default PartsByCategory;
